import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Label, TextInput, Select } from "flowbite-react";
import { PiUserCircleGearDuotone, PiFloppyDiskDuotone, PiTrashDuotone, PiWarningDuotone } from "react-icons/pi";
import { getUser } from "../../helpers/AuthService";
import { useResource } from "../../context/AppContext";
import { useNavigate } from "react-router-dom";

import api from "../../helpers/Api";
import Loading from "../../components/Loading";
import moment from "moment";
import toast from "react-hot-toast";

const schema = z.object({
    first_name: z.string().min(1, "El nombre es requerido"),
    last_name: z.string().min(1, "El apellido es requerido"),
    email: z.string().email("Correo inválido"),
    gender: z.enum(["male", "female", "other"], { required_error: "Selecciona un género" }),
    birth_date: z.string().min(1, "La fecha de nacimiento es requerida"),
    cellphone: z.string().min(7, "Teléfono inválido"),
    password: z.string().optional(),
});

export default function MyAccount() {
    const [changePassword, setChangePassword] = useState(false);
    const [userId, setUserId] = useState();
    const { userLogged, setUserLogged } = useResource();
    let navigate = useNavigate();

    const [showDelete, setShowDelete] = useState(false);
    const [preview, setPreview] = useState(null);
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [confirmValue, setConfirmValue] = useState("");
    const [reassignEmails, setReassignEmails] = useState({});

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue
    } = useForm({
        resolver: zodResolver(schema),
        defaultValues: {
            first_name: "",
            last_name: "",
            email: "",
            gender: "",
            birth_date: "",
            cellphone: "",
            password: ""
        }
    });

    useEffect(() => {
        const fetchAllData = async () => {
            try {
                const userLoggedResponse = await getUser();

                setUserId(userLoggedResponse.id)

                const userResponse = await api.get(`Customer`)
                if (userResponse.data) {
                    setValue('first_name', userResponse.data.first_name)
                    setValue('last_name', userResponse.data.last_name)
                    setValue('email', userResponse.data.email)
                    setValue('gender', userResponse.data.gender)
                    setValue('birth_date', moment.utc(userResponse.data.birth_date).format("YYYY-MM-DD"))
                    setValue('cellphone', userResponse.data.cellphone)
                }

            } catch (err) {
                if (err.response?.status === 401) {
                    navigate(`/`);
                }
            }
        };
        fetchAllData();
    }, [userLogged]);

    if (!userId) {
        return <Loading text="Cargando perfil..." />;
    }

    const onSubmit = async (data) => {
        if (!changePassword) {
            delete data.password;
        }
        data.id = userId

        const userEdited = await api.put(`Customer`, data)

        if (userEdited.data) {
            toast.success(userEdited.data.message)
        } else {
            toast.error(userEdited.data.message)
        }
    };

    const openDeleteFlow = async () => {
        try {
            const { data } = await api.get("Customer/Account");
            setPreview(data);
            setConfirmValue("");
            setReassignEmails({});
            setShowDelete(true);
        } catch (err) {
            toast.error("No se pudo cargar la información de tu cuenta");
        }
    };

    const refreshPreview = async () => {
        try {
            const { data } = await api.get("Customer/Account");
            setPreview(data);
        } catch (_) { /* noop */ }
    };

    const handleReassign = async (ticketId) => {
        const email = (reassignEmails[ticketId] || "").trim().toLowerCase();
        if (!email) { toast.error("Escribe el correo de la persona"); return; }
        try {
            await api.post("Ticket/Transfer", { ticketid: ticketId, email });
            toast.success("Boleto reasignado");
            setReassignEmails((m) => ({ ...m, [ticketId]: "" }));
            await refreshPreview();
        } catch (err) {
            toast.error(err.response?.data?.error || "No se pudo reasignar el boleto");
        }
    };

    const handleDelete = async () => {
        if (!preview) return;
        const payload = { confirm: true };
        if (preview.confirmMethod === "password") payload.password = confirmValue;
        else payload.confirmEmail = confirmValue;

        setDeleteBusy(true);
        try {
            await api.delete("Customer/Account", { data: payload });
            toast.success("Tu cuenta fue eliminada");
            setUserLogged?.(null);
            setTimeout(() => { window.location.href = "/"; }, 900);
        } catch (err) {
            toast.error(err.response?.data?.error || "No se pudo eliminar la cuenta");
        } finally {
            setDeleteBusy(false);
        }
    };

    return (
        <div className="flex flex-col justify-center items-center min-h-screen bg-[#121212] p-4 gap-6">
            <form
                onSubmit={handleSubmit(onSubmit)}
                className="w-full max-w-4xl p-8 rounded-lg shadow-md space-y-6 bg-[#181818]"
            >
                <div className="text-center place-items-center">
                    <PiUserCircleGearDuotone size={66} className="mr-2" />
                    <h2 className="text-4xl font-bold text-white text-center font-display">Mi Cuenta</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <Label className="text-white" htmlFor="first_name" value="Nombre(s)" />
                        <TextInput id="first_name" {...register("first_name")} color={errors.first_name ? "failure" : "gray"} />
                        {errors.first_name && <p className="text-red-500 text-sm">{errors.first_name.message}</p>}
                    </div>

                    <div>
                        <Label className="text-white" htmlFor="last_name" value="Apellido(s)" />
                        <TextInput id="last_name" {...register("last_name")} color={errors.last_name ? "failure" : "gray"} />
                        {errors.last_name && <p className="text-red-500 text-sm">{errors.last_name.message}</p>}
                    </div>

                    <div>
                        <Label className="text-white" htmlFor="email" value="Correo" />
                        <TextInput type="email" id="email" {...register("email")} color={errors.email ? "failure" : "gray"} />
                        {errors.email && <p className="text-red-500 text-sm">{errors.email.message}</p>}
                    </div>

                    <div>
                        <Label className="text-white" htmlFor="gender" value="Genero" />
                        <Select id="gender" {...register("gender")} color={errors.gender ? "failure" : "gray"}>
                            <option value="">Genero</option>
                            <option value="male">Masculino</option>
                            <option value="female">Femenino</option>
                            <option value="other">Otro</option>
                        </Select>
                        {errors.gender && <p className="text-red-500 text-sm">{errors.gender.message}</p>}
                    </div>

                    <div>
                        <Label className="text-white" htmlFor="birth_date" value="Fecha de Nacimiento" />
                        <TextInput type="date" id="birth_date" {...register("birth_date")} color={errors.birth_date ? "failure" : "gray"} />
                        {errors.birth_date && <p className="text-red-500 text-sm">{errors.birth_date.message}</p>}
                    </div>

                    <div>
                        <Label className="text-white" htmlFor="cellphone" value="Telefono" />
                        <TextInput type="tel" id="cellphone" {...register("cellphone")} color={errors.cellphone ? "failure" : "gray"} />
                        {errors.cellphone && <p className="text-red-500 text-sm">{errors.cellphone.message}</p>}
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        id="changePassword"
                        checked={changePassword}
                        onChange={() => setChangePassword(!changePassword)}
                        className="w-4 h-4"
                    />
                    <Label className="text-white" htmlFor="changePassword" value="Cambiar contraseña (disponible solo para cuentas creadas sin Google)" />
                </div>

                {changePassword && (
                    <div>
                        <Label className="text-white" htmlFor="password" value="Contraseña" />
                        <TextInput type="password" id="password" {...register("password")} color={errors.password ? "failure" : "gray"} />
                        {errors.password && <p className="text-red-500 text-sm">{errors.password.message}</p>}
                    </div>
                )}

                <Button type="submit" className="w-full event-one__btn thm-btn">
                    <span className="flex items-center">
                        <PiFloppyDiskDuotone size={22} className="mr-2" />Guardar
                    </span>
                </Button>
            </form>

            {/* Zona de peligro: eliminar cuenta */}
            <div className="w-full max-w-4xl p-6 rounded-lg border border-brand/40 bg-[#181818]">
                <h3 className="text-lg font-bold text-white font-display flex items-center gap-2">
                    <PiWarningDuotone className="text-brand" size={22} /> Eliminar mi cuenta
                </h3>
                <p className="text-sm text-gray-400 mt-1">
                    Se desactivará tu cuenta. Si tienes boletos de eventos próximos sin usar los perderás
                    (puedes reasignarlos antes). Podrás reactivarla registrándote de nuevo con el mismo correo.
                </p>
                <button
                    type="button"
                    onClick={openDeleteFlow}
                    className="mt-4 inline-flex items-center gap-2 rounded-lg border border-brand text-brand hover:bg-brand hover:text-white transition-colors px-4 py-2 text-sm font-semibold"
                >
                    <PiTrashDuotone size={18} /> Eliminar mi cuenta
                </button>
            </div>

            {showDelete && preview && (
                <div
                    className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4"
                    onMouseDown={(e) => { if (e.target === e.currentTarget && !deleteBusy) setShowDelete(false); }}
                >
                    <div role="dialog" aria-modal="true" className="w-full max-w-lg max-h-[90dvh] overflow-y-auto rounded-2xl bg-[#181818] border border-white/10 p-6">
                        <h3 className="text-xl font-bold text-white font-display">Eliminar tu cuenta</h3>

                        {preview.total > 0 ? (
                            <div className="mt-3 rounded-xl border border-brand/40 bg-brand/10 p-3">
                                <p className="text-sm text-white font-semibold flex items-center gap-2">
                                    <PiWarningDuotone className="text-brand" /> Perderás {preview.total} boleto(s) de eventos próximos.
                                </p>
                                <p className="text-xs text-gray-400 mt-1">{preview.note}</p>
                                <ul className="mt-3 space-y-3">
                                    {preview.atRisk.map((t) => (
                                        <li key={t.id} className="rounded-lg bg-black/30 border border-white/10 p-3">
                                            <div className="text-sm text-white font-semibold">{t.event_name || "Evento"}</div>
                                            <div className="text-xs text-gray-400">
                                                {t.package_name ? `${t.package_name} · ` : ""}{t.label ? `Asiento ${t.label}` : "Entrada general"}
                                            </div>
                                            <div className="mt-2 flex gap-2">
                                                <input
                                                    type="email"
                                                    aria-label={`Correo para reasignar el boleto de ${t.event_name || "evento"}`}
                                                    placeholder="correo de la persona"
                                                    value={reassignEmails[t.id] || ""}
                                                    onChange={(e) => setReassignEmails((m) => ({ ...m, [t.id]: e.target.value }))}
                                                    className="flex-1 min-w-0 rounded-lg border border-white/15 bg-white/5 text-white text-sm px-3 py-2 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand/40"
                                                />
                                                <button type="button" onClick={() => handleReassign(t.id)} className="rounded-lg bg-white/10 hover:bg-white/15 text-white text-sm px-3 py-2 whitespace-nowrap">
                                                    Reasignar
                                                </button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : (
                            <p className="mt-3 text-sm text-gray-400">No tienes boletos de eventos próximos en riesgo.</p>
                        )}

                        <div className="mt-5">
                            <label htmlFor="delete-confirm" className="block text-sm text-white mb-1">
                                {preview.confirmMethod === "password" ? "Confirma tu contraseña" : "Escribe tu correo para confirmar"}
                            </label>
                            <input
                                id="delete-confirm"
                                type={preview.confirmMethod === "password" ? "password" : "email"}
                                value={confirmValue}
                                onChange={(e) => setConfirmValue(e.target.value)}
                                autoComplete={preview.confirmMethod === "password" ? "current-password" : "email"}
                                className="w-full rounded-lg border border-white/15 bg-white/5 text-white text-sm px-3 py-2 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand/40"
                            />
                        </div>

                        <div className="mt-6 flex justify-end gap-3">
                            <button type="button" disabled={deleteBusy} onClick={() => setShowDelete(false)} className="rounded-lg border border-white/15 text-gray-200 hover:bg-white/10 px-4 py-2 text-sm font-semibold">
                                Cancelar
                            </button>
                            <button type="button" disabled={deleteBusy || !confirmValue} onClick={handleDelete} className="rounded-lg bg-brand hover:bg-brand-hover text-white px-4 py-2 text-sm font-semibold disabled:opacity-60">
                                {deleteBusy ? "Eliminando..." : "Eliminar definitivamente"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
